/**
 * Central object used for Store Agreement module
 */
import i18n from '@/plugins/i18n'
import _ from 'lodash'
import { useAuthStore } from '@/stores/auth'
import Agreement from '@/api/Dara/Agreement'
import AgreementVersion from '@/api/Dara/AgreementVersion'
import type ICreditTerm from '@/models/CreditTerm/CreditTerm'
import type IPricingRegion from '@/models/PricingRegion/PricingRegion'
import type ILicencePricing from '@/models/LicencePricing/LicencePricing'

interface AmendmentDiff {
  new: string | number | null
  old: string | number | null
  title: string | null
}

interface NamedLineCompareData extends Domain.Agreements.ValueObjects.LineCompareData {
  licenceTypeName: string
}

interface AgreementState {
  agreement: null |  Domain.Agreements.Resources.ApiV3.AgreementResource
  agreementAmendment: null |  Domain.Agreements.Resources.ApiV3.AgreementVersionResource
  agreementAmendmentId: null | number
  amendmentDiffs: AmendmentDiff[]
  amendmentPricingRegions: null | IPricingRegion[]
  contractLengthOptions: null | object[]
  creditTermOptions: null | ICreditTerm[]
  loading: boolean
  licencePricings: ILicencePricing[]
}

export const useAgreementStore = defineStore('Agreement', {
  state: (): AgreementState => ({
    agreement: null,
    agreementAmendment: null,
    agreementAmendmentId: null,
    amendmentDiffs: [],
    amendmentPricingRegions: null,
    contractLengthOptions: null,
    creditTermOptions: null,
    loading: true,
    licencePricings: [],
  }),

  getters: {
    amendmentLines(): NamedLineCompareData[] {
      if(!this.agreementAmendment?.diff_with_previous.lines) {
        return []
      }

      return this.agreementAmendment.diff_with_previous
        .lines
        .map((line: Domain.Agreements.ValueObjects.LineCompareData) => {
          const licenceTypeObject = this.agreement?.lines.find(
            agreementLine => agreementLine.licence_type_id === line.licence_type_id,
          )

          const licenceTypeName = licenceTypeObject?.licence_type?.long_name ?? ''

          return Object.assign({}, line, { licenceTypeName: licenceTypeName })
        })
    },
  },

  actions: {
    async getAgreement() {
      const authStore = useAuthStore()
      if(authStore.organisation) {
        const agreementResponseData = await Agreement.getAgreement()
        if(agreementResponseData) {
          this.agreement = agreementResponseData.data
          this.agreement.lines = _.orderBy(
            this.agreement.lines,
            ['licence_type.display_order'],
          )
          this.contractLengthOptions = agreementResponseData.additional.contract_length_options
          this.agreementAmendmentId = agreementResponseData.additional.agreement_amendment_id
          if(this.agreementAmendmentId) {
            const agreementVersionResponseData = await AgreementVersion.getAgreementVersion()

            this.agreementAmendment = agreementVersionResponseData.data
            this.amendmentPricingRegions = agreementVersionResponseData.additional.pricing_regions
            this.creditTermOptions = agreementVersionResponseData.additional.credit_terms
            this.createAmendmentDiffs()
          }
        }
      }
      this.loading = false
    },
    createAmendmentDiff(mapping: {key: string, titleKey: string}) {
      const diff: AmendmentDiff = { new: null, old: null, title: i18n.global.t(mapping.titleKey) }

      if (mapping.key === 'discount_percentage' && this.agreementAmendment?.diff_with_previous.discount_percentage) {
        diff.new = this.agreementAmendment?.diff_with_previous.discount_percentage.new + '%'
        diff.old = this.agreementAmendment?.diff_with_previous.discount_percentage.old + '%'
      } else if (mapping.key === 'extra_order_invoice_number' && this.agreementAmendment?.diff_with_previous.extra_order_invoice_number) {
        diff.new = this.agreementAmendment?.diff_with_previous.extra_order_invoice_number.new === 1
          ? i18n.global.t('pages.agreement.amendment_modal.annually')
          : i18n.global.t('pages.agreement.amendment_modal.quarterly')
        diff.old = this.agreementAmendment?.diff_with_previous.extra_order_invoice_number.old === 1
          ? i18n.global.t('pages.agreement.amendment_modal.annually')
          : i18n.global.t('pages.agreement.amendment_modal.quarterly')
      } else if (mapping.key === 'length' && this.agreementAmendment?.diff_with_previous.length) {
        diff.new = this.agreementAmendment?.diff_with_previous.length.new + ' ' + i18n.global.t('general.years')
        diff.old = this.agreementAmendment?.diff_with_previous.length.old + ' ' + i18n.global.t('general.years')
      }
      this.amendmentDiffs.push(diff)
    },
    createAmendmentDiffs() {
      this.amendmentDiffs = []

      const diffMappings: Array<{ key: string, titleKey: string }> = []

      const potentialDiffMappings = [
        {
          key: 'credit_term_id',
          titleKey: 'pages.agreement.amendment_modal.credit_term',
        },
        {
          key: 'discount_percentage',
          titleKey: 'fields.discount_percentage',
        },
        {
          key: 'extra_order_invoice_number',
          titleKey: 'pages.agreement.amendment_modal.additional_orders_invoiced',
        },
        {
          key: 'is_auto_renewing',
          titleKey: 'pages.agreement.amendment_modal.auto_renewing',
        },
        {
          key: 'length',
          titleKey: 'fields.length',
        },
        {
          key: 'pricing_region_id',
          titleKey: 'fields.pricing_region',
        },
      ]

      potentialDiffMappings.forEach(potentialDiffMapping => {
        if(this.agreementAmendment?.diff_with_previous.hasOwnProperty(potentialDiffMapping.key)) {
          diffMappings.push(potentialDiffMapping)
        }
      })

      diffMappings.forEach(mapping => {
        if(mapping.key === 'credit_term_id') {
          this.createCreditTermDiff(mapping)
        } else if (mapping.key === 'pricing_region_id') {
          this.createPricingRegionDiff(mapping)
        } else if (mapping.key === 'is_auto_renewing') {
          this.createAutoRenewingDiff(mapping)
        } else {
          this.createAmendmentDiff(mapping)
        }
      })
    },
    createCreditTermDiff(mapping: {key: string, titleKey: string}) {
      const amendmentCreditTermIds = this.agreementAmendment?.diff_with_previous.credit_term_id

      if (!Array.isArray(amendmentCreditTermIds)) {
        this.amendmentDiffs.push({
          new: this.creditTermOptions?.find(creditTerm => creditTerm.id === amendmentCreditTermIds?.new)?.terms ?? null,
          old: this.creditTermOptions?.find(creditTerm => creditTerm.id === amendmentCreditTermIds?.old)?.terms ?? null,
          title: i18n.global.t(mapping.titleKey),
        })
      }
    },
    createAutoRenewingDiff(mapping: {key: string, titleKey: string}) {
      const autoRenewingDiff = this.agreementAmendment?.diff_with_previous.is_auto_renewing

      if (!Array.isArray(autoRenewingDiff)) {
        this.amendmentDiffs.push({
          new: autoRenewingDiff?.new ? i18n.global.t('general.enabled') : i18n.global.t('general.disabled'),
          old: autoRenewingDiff?.old ? i18n.global.t('general.enabled') : i18n.global.t('general.disabled'),
          title: i18n.global.t(mapping.titleKey),
        })
      }
    },
    createPricingRegionDiff(mapping: {key: string, titleKey: string}) {
      const pricingRegionDiff = this.agreementAmendment?.diff_with_previous.pricing_region_id

      if (!Array.isArray(pricingRegionDiff)) {
        this.amendmentDiffs.push({
          new: this.amendmentPricingRegions?.find(region => region.id === pricingRegionDiff?.new)?.name ?? null,
          old: this.amendmentPricingRegions?.find(region => region.id === pricingRegionDiff?.old)?.name ?? null,
          title: i18n.global.t(mapping.titleKey),
        })
      }
    },
  },
})
